import { OperationFinancialInstrumentEnumFR } from '@app/models/api/enums/OperationFinancialInstrumentEnum';
import { OperationStatusEnumFR } from '@app/models/api/enums/OperationStatusEnum';
import { OperationRegulationsEnum } from '@app/models/api/enums/OperationRegulationsEnum';
import { RedirectUrlStatus, RedirectUrlTransactionMethod } from '@app/enums/RedirectUrl';
import { OperationCategoryEnumFR } from '@app/models/api/enums/OperationCategoryEnum';

export const urls = {
	HOME: '/',
	DASHBOARD: '/portefeuille',
	SIGNUP: '/creer-un-compte',
	ACTIVATION: '/activation',
	USER: {
		DASHBOARD: '/portefeuille/tableau-de-bord',
		ACCOUNT: {
			INDEX: '/compte',
			PROFILE: '/compte/profil',
			DOCUMENTS: '/compte/documents',
			PREFERENCES: '/compte/preferences',
			MATCHING_PSFP: '/compte/adequation/psfp',
			MATCHING_PSI: '/compte/adequation/psi',
			SECURITY: '/compte/securite'
		},
		PORTFOLIO: {
			INDEX: '/portefeuille',
			DASHBOARD: '/portefeuille/tableau-de-bord',
			ACTIONS: '/portefeuille/actions',
			OBLIGATIONS: '/portefeuille/obligations',
			EQUITY_SHARES: '/portefeuille/titres-participatifs',
			FUNDS: '/portefeuille/fonds'
		},
		WALLET: {
			PATH: '/compte-de-paiement',
			INDEX: '/compte-de-paiement/historique',
			MONEY_IN: '/compte-de-paiement/recharge',
			MONEY_OUT: '/compte-de-paiement/virement'
		},
		TAXATION: {
			INDEX: '/fiscalite',
			TAX_CERTIFICATES: '/fiscalite/attestations-fiscales',
			OWNERSHIP_CERTIFICATES: '/fiscalite/attestations-propriete',
			DPFNL: '/fiscalite/dispense-prelevement-forfaitaire-non-liberatoire',
			NON_RESIDENTS: '/fiscalite/non-residents'
		},
		GHOST_LOGIN: '/ghost'
	},
	PORTFOLIO: {
		INDEX: '/souscription',
		SUMMARY: (id: string): string => {
			return `/souscription/${id}/recapitulatif`;
		},
		ECHEANCIER_HISTORY: (subscriptionID: string, echeancierID: string): string => {
			return `/souscription/${subscriptionID}/echeancier/${echeancierID}/historique`;
		},
		ECHEANCIER: (id: string): string => {
			return `/souscription/${id}/echeancier`;
		},
		REPORTINGS: (id: string): string => {
			return `/souscription/${id}/reportings`;
		},
		REPORTING: (subscriptionId: string, reportingId: string): string => {
			return `/souscription/${subscriptionId}/reportings/${reportingId}`;
		},
		GENERAL_ASSEMBLY: (subscriptionId: string | number, id?: string | number): string => {
			return `/souscription/${subscriptionId}/assemblees${id ? `/${id}` : ''}`;
		},
		GENERAL_ASSEMBLY_WISEED: (assemblyId: number) =>
			`/portefeuille/wiseed/assemblees/${assemblyId}`,
		NEWS: (id: string): string => {
			return `/souscription/${id}/news`;
		},
		QUESTION: (id: string): string => {
			return `/souscription/${id}/questions`;
		},
		POLLS: (subscriptionId: string, id?: string): string => {
			return `/souscription/${subscriptionId}/sondages${id ? `/${id}` : ''}`;
		},
		EMAILINGS: (id: string): string => {
			return `/souscription/${id}/communications`;
		},
		EMAILING: (subscriptionId: string, emailingId: string): string => {
			return `/souscription/${subscriptionId}/communications/${emailingId}`;
		},
		DOCUMENTS: (subscriptionId: string): string => {
			return `/souscription/${subscriptionId}/documents`;
		}
	},
	PAYMENT_ACCOUNT: {
		TRANSACTIONS: (): string => {
			return '/compte/compte-de-paiement/transactions';
		},
		MONEY_IN_CREDIT_CARD_REDIRECT_URL_SUCCESS: (baseUrl: string): string => {
			return `${baseUrl}/${RedirectUrlTransactionMethod.CARD}/${RedirectUrlStatus.SUCCESS}`;
		},
		MONEY_IN_CREDIT_CARD_REDIRECT_URL_FAIL: (baseUrl: string): string => {
			return `${baseUrl}/${RedirectUrlTransactionMethod.CARD}/${RedirectUrlStatus.ERROR}`;
		},
		MONEY_IN_SIMPLIFIED_SDD_REDIRECT_URL_SUCCESS: (baseUrl: string): string => {
			return `${baseUrl}/${RedirectUrlTransactionMethod.SSDD}/${RedirectUrlStatus.SUCCESS}`;
		},
		MONEY_IN_SIMPLIFIED_SDD_REDIRECT_URL_FAIL: (baseUrl: string): string => {
			return `${baseUrl}/${RedirectUrlTransactionMethod.SSDD}/${RedirectUrlStatus.ERROR}`;
		},
		MONEY_OUT_REDIRECT_URL_SUCCESS: (baseUrl: string): string => {
			return `${baseUrl}/${RedirectUrlTransactionMethod.MONEY_OUT}/${RedirectUrlStatus.SUCCESS}`;
		},
		MONEY_OUT_REDIRECT_URL_FAIL: (baseUrl: string): string => {
			return `${baseUrl}/${RedirectUrlTransactionMethod.MONEY_OUT}/${RedirectUrlStatus.ERROR}`;
		}
	},
	SIGNATURE_REDIRECT_URL_SUCCESS: (baseUrl: string): string => {
		return `${baseUrl}/signee`;
	},
	SIGNATURE_REDIRECT_URL_ERROR: (baseUrl: string): string => {
		return `${baseUrl}/erreur-signature`;
	},
	SUBSCRIBE: {
		PATHBASE: '/investir',
		QUESTIONS: (id: string): string => {
			return `/investir/${id}/questions-instrument-financier`;
		},
		QUEUE: (id: string): string => {
			return `/investir/${id}/file-attente`;
		},
		CONDITIONS: (id: string): string => {
			return `/investir/${id}/conditions`;
		},
		TERMS: (id: string): string => {
			return `/investir/${id}/modalites`;
		},
		INFORMATION: (operationId: string, subscriptionId: string): string => {
			return `/investir/${operationId}/informations/${subscriptionId}`;
		},
		STATUS: (operationId: string, subscriptionId: string): string => {
			return `/investir/${operationId}/souscription-enregistree${
				subscriptionId ? `/${subscriptionId}` : ''
			}`;
		},
		CREDIT_CARD_PAYMENT_REDIRECT_URL_SUCCESS: (baseUrl: string): string => {
			return `${baseUrl}/paye-cb`;
		},
		CREDIT_CARD_PAYMENT_REDIRECT_URL_ERROR: (baseUrl: string): string => {
			return `${baseUrl}/erreur-paiement-cb`;
		},
		SIMPLIFIED_SDD_PAYMENT_REDIRECT_URL_SUCCESS: (baseUrl: string): string => {
			return `${baseUrl}/paye-ssdd`;
		},
		SIMPLIFIED_SDD_PAYMENT_REDIRECT_URL_ERROR: (baseUrl: string): string => {
			return `${baseUrl}/erreur-paiement-bridge`;
		}
	},
	KYC: `/inscription`,
	KYC_REGULATION: (regulation: OperationRegulationsEnum): string =>
		`/inscription?regulation=${regulation}`,
	KYC_CONFIRMATION: '/inscription/confirmation',
	PROJECT: {
		PATHBASE: '/projet/',
		PRIVATE_ACCESS: '/projet/acces-prive',
		INDEX: (id: string) => `/projet/${id}`,
		VOTE: (id: string, rating: number) => `/projet/${id}?note=${rating}`,
		NEWS: (id: string) => `/projet/${id}/news`,
		QUESTIONS: (id: string) => `/projet/${id}/questions`,
		TESTIMONIES: (id: string) => `/projet/${id}/temoignages`,
		LIVES: (id: string) => `/projet/${id}/lives`,
		PROMOTER: (id: string) => `/projet/${id}/promoteur`,
		PARTNER: (id: string) => `/projet/${id}/partenaire`,
		PRODUCTOR: (id: string) => `/projet/${id}/producteur`,
		DETAILS: (id: string) => `/projet/${id}/details`,
		MANAGEMENT_COMPANY: (id: string) => `/projet/${id}/societe-de-gestion`
	},
	PROJECTS: {
		INDEX: '/projets',
		STOCKS: `/projets/crowdfunding?statuts=${OperationStatusEnumFR.EN_FINANCEMENT}&instruments=${OperationFinancialInstrumentEnumFR.ACTIONS}`,
		EQUITY_SECURITIES: `/projets/crowdfunding?statuts=${OperationStatusEnumFR.EN_FINANCEMENT}&instruments=${OperationFinancialInstrumentEnumFR.TITRES_PARTICIPATIFS}`,
		BONDS: `/projets/crowdfunding?statuts=${OperationStatusEnumFR.EN_FINANCEMENT}&instruments=${OperationFinancialInstrumentEnumFR.OBLIGATIONS}`,
		AIF_SHARES: `/projets/fonds?statuts=${OperationStatusEnumFR.EN_FINANCEMENT}`,
		FUNDED: `/projets/crowdfunding?statuts=${OperationStatusEnumFR.FINANCE}`,
		REFUNDED: `/projets/crowdfunding?statuts=${OperationStatusEnumFR.SORTIE}`,
		PSFP: `/projets/crowdfunding?statuts=${OperationStatusEnumFR.EN_FINANCEMENT}`,
		REAL_ESTATE: `/projets/crowdfunding?statuts=${OperationStatusEnumFR.EN_FINANCEMENT}&categories=${OperationCategoryEnumFR.IMMOBILIER}`,
		HEALTH: `/projets/crowdfunding?statuts=${OperationStatusEnumFR.EN_FINANCEMENT}&categories=${OperationCategoryEnumFR.SANTE}`,
		ENERGY_TRANSITION: `/projets/crowdfunding?statuts=${OperationStatusEnumFR.EN_FINANCEMENT}&categories=${OperationCategoryEnumFR.IMPACT_NATURE}`,
		TECH_AND_INNOVATION: `/projets/crowdfunding?statuts=${OperationStatusEnumFR.EN_FINANCEMENT}&categories=${OperationCategoryEnumFR.TECH_INNOVATION}`,
		PSFP_EXCEPT_REAL_ESTATE: `/projets/crowdfunding?statuts=${OperationStatusEnumFR.EN_FINANCEMENT}&categories=${OperationCategoryEnumFR.IMPACT_NATURE},${OperationCategoryEnumFR.SANTE},${OperationCategoryEnumFR.TECH_INNOVATION}`,
		PRIVATE: `/projets/acces-prives?statuts=${OperationStatusEnumFR.EN_FINANCEMENT}`
	},
	INVESTORS: {
		HOW_TO_INVEST: '/investisseurs/comment-investir',
		WHY_INVEST: '/investisseurs/pourquoi-investir',
		SIMULATOR: '/investisseurs/estimez-la-rentabilite-de-vos-investissements',
		TAXATION: '/investisseurs/reduisez-vos-impots',
		HELP: 'https://aide.wiseed.com/hc/fr'
	},
	COMPANIES: {
		COLLECT: {
			HOW_TO: '/entreprises/levez-des-fonds/comment-lever-des-fonds',
			HEALTH: '/entreprises/levez-des-fonds/deposez-un-dossier-sante',
			ENERGY_TRANSITION: '/entreprises/levez-des-fonds/deposez-un-dossier-transition-energetique',
			REAL_ESTATE: '/entreprises/levez-des-fonds/deposez-un-dossier-immobilier'
		},
		BUSINESS_PROVIDER: '/entreprises/devenez-apporteur-d-affaires',
		SUCCESSFUL_FUNDRAISING: '/entreprises/reussissez-votre-levee',
		SETUP_A_CLUB_DEAL: '/entreprises/montez-un-clubdeal',
		BECOME_A_TIED_AGENT: '/entreprises/devenez-agent-lie'
	},
	ABOUT: {
		OUR_MISSION: '/a-propos/notre-mission',
		BLOG: '/blog',
		CONTACT: '/a-propos/contact'
	},
	STATS: '/statistiques',
	PERFORMANCE_INDICATORS:
		'https://wiseed-medias.s3.eu-west-1.amazonaws.com/Indicateurs_de_performance_FPF_00922.pdf',
	EVENTS: '/evenements',
	JOIN_US: 'https://www.welcometothejungle.com/fr/companies/wiseed',
	PARTNERS: '/partenaires',
	LEGAL: {
		TERMS_OF_USE: '/juridique/conditions-d-utilisation',
		PRIVACY_POLICY: '/juridique/politique-de-confidentialite',
		LEGAL_NOTICE: '/juridique/mentions-legales',
		RISK_WARNING: '/juridique/avertissements-sur-les-risques',
		CONSENT: '/juridique/consentement',
		CLAIM: '/juridique/reclamation'
	},
	NEWS_FEED: '/fil-actualite',
	BLOG: {
		HOME: '/blog',
		CATEGORIES: '/blog/categories',
		CATEGORIE: (slug: string) => `/blog/categories/${slug}`,
		ARTICLES: '/blog/articles/',
		ARTICLE: (slug: string) => `/blog/articles/${slug}`,
		ARTICLE_INVESTOR_FEES: '/blog/articles/frais-investisseurs'
	}
};
